import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import dayjs, { DATE_FORMATS } from '../../utils/dateUtils';

import {
  FOOTPRINT_ALL_VIEWS,
  RELEVANCE_ALL_VIEWS,
  SONAR_PROJECT_RANGE_TYPE,
  SONAR_RANGE_TYPE,
  SONAR_SECTIONS,
  SONAR_TABS,
  SORT_ORDER,
  VIEWS,
} from '../../constants';
import constructEntriesLabel from '../../utils/constructEntriesLabel';
import { createGroupsData } from '../../utils/groupsProcessor';
import {
  consumerSonarLogin,
  getConsumerSonarData,
  getConsumerSonarNavig8,
  replyConsumerSonarNavig8,
} from '../consumer/sonar/consumerSonarActions';
import {
  getSonarCollectDataStatus,
  getSonarData,
  getSonarNavig8,
  replySonarNavig8,
  setChartView,
  setChartViewGroup,
} from './sonarActions';

const { INTEREST, TRUST, FRICTION, PASSION, RELEVANCE, FOOTPRINT, BRAND } = SONAR_SECTIONS;
const { NARROW, ALL, TOP_10 } = VIEWS;

const initialState = {
  domainEntries: [],
  groupEntries: [],
  isLoading: false,
  labelSettings: {
    groupNames: false,
    countryCode: false,
  },
  isShowGlobalRank: false,
  columns: [
    {
      key: 'interest',
      title: 'interest',
    },
    {
      key: 'trust',
      title: 'trust',
    },
    {
      key: 'passion',
      title: 'passion',
    },
    {
      key: 'relevanceStrength',
      title: 'relevanceStrength',
    },
    {
      key: 'relevanceBreadth',
      title: 'relevanceBreadth',
    },
    {
      key: 'friction',
      title: 'rivalry',
    },
    {
      key: 'presence',
      title: 'reach',
    },
    {
      key: 'energy',
      title: 'vitality',
    },
    {
      key: 'powerrank',
      title: 'powerrank',
    },
  ],
  sections: {
    [INTEREST]: createSectionState(),
    [TRUST]: createSectionState([undefined, TOP_10]),
    [FRICTION]: createSectionState([undefined, TOP_10]),
    [PASSION]: createSectionState(),
    [RELEVANCE]: createSectionState([undefined, RELEVANCE_ALL_VIEWS.TOP_10_RELEVANCE_BREADTH]),
    [FOOTPRINT]: createSectionState([undefined, FOOTPRINT_ALL_VIEWS.TOP_10_PRESENCE]),
    [BRAND]: {
      domain: null,
    },
  },

  selectedTab: SONAR_TABS.DASHBOARD,
  isShowDedicated: false,
  sonarTable: {
    searchQuery: '',
    sort: {
      key: 'powerrank',
      order: SORT_ORDER.DESC,
    },
  },

  collectedDataStatus: {
    collectedTill: null,
    collectedFrom: null,
    rangeType: '',
  },

  dateRanges: {
    [SONAR_RANGE_TYPE.COMPARABLE]: {
      startDate: null,
      endDate: null,
    },
    [SONAR_RANGE_TYPE.COMPARE_TO]: {
      startDate: null,
      endDate: null,
    },
  },

  navig8: {
    thread: null,
    hint: '',
    chapters: [],
  },
  navig8AbortController: null,
};

const sonarSlice = createSlice({
  name: 'sonarDashboard',
  initialState,
  reducers: {
    updateLabelSettings: (state, action) => {
      state.labelSettings = action.payload;

      state.domainEntries = state.domainEntries.map((data) => ({
        ...data,
        label: constructEntriesLabel({
          companyName: data.companyName,
          domain: data.domain,
          groups: data.groups,
          country: data.country,
          labelSettings: action.payload,
        }),
      }));
    },
    toggleGlobalRank: (state) => {
      state.isShowGlobalRank = !state.isShowGlobalRank;
    },
    selectSonarTab: (state, action) => {
      state.selectedTab = action.payload;
    },
    changeSearchQuery: (state, action) => {
      state.sonarTable.searchQuery = action.payload;
      state.domainEntries = state.domainEntries.map((entry) => ({
        ...entry,
        selected: false,
      }));
    },
    changeSonarDataTableSort: (state, action) => {
      state.sonarTable.sort = {
        key: action.payload.key,
        order: action.payload.order,
      };
    },
    toggleDedicatedTables: (state) => {
      state.isShowDedicated = !state.isShowDedicated;
    },
    changeDedicatedTablesVisibility: (state, action) => {
      state.isShowDedicated = action.payload;
    },
    changeSonarDate: (state, action) => {
      const prevValue = state.dateRanges[action.payload.type][action.payload.key];

      if (prevValue !== action.payload.value) {
        state.dateRanges[action.payload.type][action.payload.key] = action.payload.value;
      }
    },

    selectSingleDomain: (state, action) => {
      state.domainEntries = state.domainEntries.map((entry) =>
        entry.domain === action.payload ? { ...entry, selected: !entry.selected } : entry,
      );
    },
    selectAllDomains: (state) => {
      const isAllSelected = state.domainEntries.every(({ selected }) => selected);
      state.domainEntries = state.domainEntries.map((entry) => ({
        ...entry,
        selected: !isAllSelected,
      }));
    },
    toggleSingleDomainVisibility: (state, action) => {
      state.domainEntries = state.domainEntries.map((entry) =>
        entry.domain === action.payload ? { ...entry, isActive: !entry.isActive } : entry,
      );
    },
    markSelectedDomains: (state, action) => {
      const marked = action.payload;
      state.domainEntries = state.domainEntries.map((entry) => (entry.selected ? { ...entry, marked: marked } : entry)); // TODO rename to isMarked
    },
    toggleVisibilitySelectedDomains: (state, action) => {
      const isActive = action.payload;
      state.domainEntries = state.domainEntries.map((entry) =>
        entry.selected ? { ...entry, isActive: isActive } : entry,
      );
    },
    chooseDomain: (state, action) => {
      state.sections[BRAND].domain = action.payload;
      state.selectedTab = SONAR_TABS.BRAND;
    },
    changeChartView: (state, action) => {
      const { section, chartIndex, view } = action.payload;
      state.sections[section].charts[chartIndex].view = view;
    },

    setNavig8AbortController: (state, action) => {
      if (action.payload && state.navig8AbortController) {
        state.navig8AbortController.abort();
      }

      state.navig8AbortController = action.payload;
    },
    cancelNavig8: (state) => {
      if (state.navig8AbortController) {
        state.navig8AbortController.abort();
      }

      state.navig8AbortController = null;
    },

    resetSonarData: (state) => ({ ...initialState, labelSettings: state.labelSettings }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSonarCollectDataStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSonarCollectDataStatus.rejected, (state) => {
        state.isLoading = false;
      })

      .addCase(setChartView.fulfilled, (state, action) => {
        const { section, index, view } = action.payload;
        state.sections[section].charts[index].view = view;
        if (state.sections[section].charts[index].group) {
          state.sections[section].charts[index].group = 'ALL';
        }
      })
      .addCase(setChartViewGroup.fulfilled, (state, action) => {
        const { section, index, group } = action.payload;
        state.sections[section].charts[index].group = group;
      })

      .addMatcher(isAnyOf(getSonarNavig8.fulfilled, getConsumerSonarNavig8.fulfilled), (state, action) => {
        const response = action.payload.response;
        const { hint: newHint, ...chaptersData } = response;

        const formattedChapters = Object.entries(chaptersData)
          .map(([key, value], index) => ({
            count: index + 1,
            type: key,
            message: value,
            isNew: false,
          }))
          .reverse();

        state.navig8.hint = newHint;
        state.navig8.thread = action.payload.thread;
        state.navig8.chapters = formattedChapters;
      })

      .addMatcher(isAnyOf(replySonarNavig8.fulfilled, replyConsumerSonarNavig8.fulfilled), (state, action) => {
        const response = action.payload.response;
        const { hint: newHint, ...chaptersData } = response;

        const formattedOldChapters = state.navig8.chapters.map((chapter) => ({
          ...chapter,
          isNew: false,
        }));

        const formattedNewChapters = Object.entries(chaptersData)
          .map(([key, value], index) => ({
            count: state.navig8.chapters.length + index + 1,
            type: key,
            message: value,
            isNew: true,
          }))
          .reverse();

        state.navig8.hint = newHint;
        state.navig8.thread = action.payload.thread;
        state.navig8.chapters = [...formattedNewChapters, ...formattedOldChapters];
      })

      .addMatcher(isAnyOf(getSonarCollectDataStatus.fulfilled, consumerSonarLogin.fulfilled), (state, action) => {
        state.isLoading = false;
        state.collectedDataStatus.collectedTill = action.payload.collectedTill;
        state.collectedDataStatus.collectedFrom = action.payload.collectedFrom;

        const rangeType = action.payload.rangeType;

        state.collectedDataStatus.rangeType = rangeType;

        if (
          action.payload.collectedTill &&
          action.payload.collectedFrom &&
          rangeType === SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE
        ) {
          const compareToDate = dayjs(action.payload.collectedTill, DATE_FORMATS.API_DATE)
            .subtract(7, 'days')
            .format(DATE_FORMATS.API_DATE);

          state.dateRanges = {
            [SONAR_RANGE_TYPE.COMPARABLE]: {
              startDate: action.payload.collectedTill,
              endDate: action.payload.collectedTill,
            },
            [SONAR_RANGE_TYPE.COMPARE_TO]: {
              startDate: compareToDate,
              endDate: compareToDate,
            },
          };
        }

        if (
          action.payload.collectedTill &&
          action.payload.collectedFrom &&
          rangeType === SONAR_PROJECT_RANGE_TYPE.MONTHLY_RANGE
        ) {
          const collectedTill = dayjs(action.payload.collectedTill, DATE_FORMATS.API_DATE);
          const compareToDate = dayjs(action.payload.collectedTill, DATE_FORMATS.API_DATE).subtract(1, 'months');

          state.dateRanges = {
            [SONAR_RANGE_TYPE.COMPARABLE]: {
              startDate: collectedTill.startOf('month').format(DATE_FORMATS.API_DATE),
              endDate: collectedTill.endOf('month').format(DATE_FORMATS.API_DATE),
            },
            [SONAR_RANGE_TYPE.COMPARE_TO]: {
              startDate: compareToDate.startOf('month').format(DATE_FORMATS.API_DATE),
              endDate: compareToDate.endOf('month').format(DATE_FORMATS.API_DATE),
            },
          };
        }
      })

      .addMatcher(isAnyOf(getSonarData.pending, getConsumerSonarData.pending), (state) => {
        state.isLoading = true;
      })
      .addMatcher(isAnyOf(getSonarData.rejected, getConsumerSonarData.rejected), (state) => {
        state.isLoading = false;
      })
      .addMatcher(isAnyOf(getSonarData.fulfilled, getConsumerSonarData.fulfilled), (state, action) => {
        state.domainEntries = action.payload.content.map((data) => ({
          ...data,
          companyLogo: data.initDataId ?? '',
          companyName: data.brandCompany,
          isActive: true,
          selected: false,
          marked: false,
          label: constructEntriesLabel({
            companyName: data.brandCompany,
            domain: data.domain,
            groups: data.groups,
            country: data.country,
            labelSettings: state.labelSettings,
          }),
        }));

        state.groupEntries = createGroupsData(action.payload.content).map((data) => ({
          ...data,
          isActive: true,
          selected: false,
          marked: false,
        }));

        state.isLoading = false;
      });
  },
});

const { actions, reducer } = sonarSlice;

export const {
  resetSonarData,
  updateLabelSettings,
  toggleGlobalRank,
  selectSonarTab,
  changeSearchQuery,
  changeSonarDataTableSort,
  toggleDedicatedTables,
  changeDedicatedTablesVisibility,
  changeSonarDate,

  selectSingleDomain,
  selectAllDomains,
  toggleSingleDomainVisibility,
  markSelectedDomains,
  toggleVisibilitySelectedDomains,
  chooseDomain,
  changeChartView,

  setNavig8AbortController,
  cancelNavig8,
} = actions;

export default reducer;

function createSectionState(views = []) {
  return {
    charts: [
      {
        view: views[0] || NARROW,
      },
      {
        view: views[1] || ALL,
        group: 'ALL',
      },
    ],
  };
}
